import React, { useState } from 'react';
import Layout from '../../components/Layout';
import classes from './Insights.module.css';

const Insights = () => {
    const [dateRange, setDateRange] = useState('Last 30 days');
    const [activeMetric, setActiveMetric] = useState('spending');
    const [isLoading, setIsLoading] = useState(false);

    // Sample data
    const spendingData = [
        { name: 'Food & Dining', value: 2400, percentage: 35 },
        { name: 'Transportation', value: 1600, percentage: 23 },
        { name: 'Shopping', value: 1200, percentage: 18 },
        { name: 'Entertainment', value: 800, percentage: 12 },
        { name: 'Other', value: 800, percentage: 12 }
    ];

    const recentTransactions = [
        { id: 1, description: 'Starbucks Coffee', category: 'Food & Dining', amount: -12.50, date: '2025-01-15', account: 'Chase Checking' },
        { id: 2, description: 'Uber Ride', category: 'Transportation', amount: -23.75, date: '2025-01-14', account: 'Credit Card' },
        { id: 3, description: 'Amazon Purchase', category: 'Shopping', amount: -89.99, date: '2025-01-14', account: 'Credit Card' },
        { id: 4, description: 'Salary Deposit', category: 'Income', amount: 4500.00, date: '2025-01-13', account: 'Chase Checking' },
        { id: 5, description: 'Netflix Subscription', category: 'Entertainment', amount: -15.99, date: '2025-01-12', account: 'Credit Card' }
    ];

    const handleAddExpense = () => {
        console.log('Add expense clicked');
    };

    const handleAddCredit = () => {
        console.log('Add credit clicked');
    };

    const handleDateChange = (newRange) => {
        setIsLoading(true);
        setDateRange(newRange);
        setTimeout(() => setIsLoading(false), 800);
    };

    const totalSpending = spendingData.reduce((sum, item) => sum + item.value, 0);

    return (
        <Layout>
            <div className={classes.insights}>
                {/* Hero Header */}
                <div className={classes.heroHeader}>
                    <div className={classes.heroContent}>
                        <div className={classes.heroText}>
                            <h1 className={classes.heroTitle}>Financial Insights</h1>
                            <p className={classes.heroSubtitle}>
                                Dive deep into your spending patterns and discover opportunities to optimize your financial health
                            </p>
                        </div>

                        <div className={classes.heroControls}>
                            <div className={classes.dateSelectWrapper}>
                                <select
                                    className={classes.premiumDateSelect}
                                    value={dateRange}
                                    onChange={(e) => handleDateChange(e.target.value)}
                                >
                                    <option>Last 7 days</option>
                                    <option>Last 30 days</option>
                                    <option>Last 3 months</option>
                                    <option>Last 6 months</option>
                                    <option>Last year</option>
                                </select>
                            </div>

                            <div className={classes.premiumActionButtons}>
                                <button
                                    className={classes.premiumAddExpenseBtn}
                                    onClick={handleAddExpense}
                                >
                                    + Add Expense
                                </button>
                                <button
                                    className={classes.premiumAddCreditBtn}
                                    onClick={handleAddCredit}
                                >
                                    + Add Credit
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={classes.heroDecorations}></div>
                </div>

                {/* Quick Stats Cards */}
                <div className={classes.quickStatsGrid}>
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}>💰</div>
                        <div className={classes.statContent}>
                            <div className={classes.statLabel}>Total Spending</div>
                            <div className={classes.statValue}>${totalSpending.toLocaleString()}</div>
                            <div className={classes.statChange}>-8.2% from last month</div>
                        </div>
                    </div>
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}>📊</div>
                        <div className={classes.statContent}>
                            <div className={classes.statLabel}>Monthly Budget</div>
                            <div className={classes.statValue}>$5,200</div>
                            <div className={classes.statChange}>12% available</div>
                        </div>
                    </div>
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}>🎯</div>
                        <div className={classes.statContent}>
                            <div className={classes.statLabel}>Savings Rate</div>
                            <div className={classes.statValue}>28.5%</div>
                            <div className={classes.statChange}>+2.1% from last month</div>
                        </div>
                    </div>
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}>⭐</div>
                        <div className={classes.statContent}>
                            <div className={classes.statLabel}>Credit Score</div>
                            <div className={classes.statValue}>785</div>
                            <div className={classes.statChange}>+5 points</div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={classes.mainContentGrid}>
                    {/* Spending Breakdown */}
                    <div className={classes.spendingSection}>
                        <div className={classes.premiumCard}>
                            <div className={classes.cardHeader}>
                                <h2 className={classes.cardTitle}>Spending Breakdown</h2>
                                <div className={classes.metricTabs}>
                                    {['spending', 'income', 'savings'].map((metric) => (
                                        <button
                                            key={metric}
                                            onClick={() => setActiveMetric(metric)}
                                            className={`${classes.metricTab} ${activeMetric === metric ? classes.metricTabActive : ''}`}
                                        >
                                            {metric.charAt(0).toUpperCase() + metric.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {isLoading ? (
                                <div className={classes.loadingContainer}>
                                    <div className={classes.premiumSpinner}></div>
                                </div>
                            ) : (
                                <div className={classes.chartContent}>
                                    <div className={classes.premiumChartContainer}>
                                        <svg width="300" height="300" viewBox="0 0 300 300" className={classes.premiumChart}>
                                            <circle
                                                cx="150"
                                                cy="150"
                                                r="80"
                                                fill="none"
                                                stroke="#8B5CF6"
                                                strokeWidth="40"
                                                strokeDasharray="176 0"
                                                strokeDashoffset="0"
                                                transform="rotate(-90 150 150)"
                                                className={classes.chartSegment}
                                            />
                                            <circle
                                                cx="150"
                                                cy="150"
                                                r="80"
                                                fill="none"
                                                stroke="#06B6D4"
                                                strokeWidth="40"
                                                strokeDasharray="115 385"
                                                strokeDashoffset="-176"
                                                transform="rotate(-90 150 150)"
                                                className={classes.chartSegment}
                                            />
                                            <circle
                                                cx="150"
                                                cy="150"
                                                r="80"
                                                fill="none"
                                                stroke="#10B981"
                                                strokeWidth="40"
                                                strokeDasharray="90 410"
                                                strokeDashoffset="-291"
                                                transform="rotate(-90 150 150)"
                                                className={classes.chartSegment}
                                            />
                                            <circle
                                                cx="150"
                                                cy="150"
                                                r="80"
                                                fill="none"
                                                stroke="#F59E0B"
                                                strokeWidth="40"
                                                strokeDasharray="60 440"
                                                strokeDashoffset="-381"
                                                transform="rotate(-90 150 150)"
                                                className={classes.chartSegment}
                                            />
                                            <circle
                                                cx="150"
                                                cy="150"
                                                r="80"
                                                fill="none"
                                                stroke="#EF4444"
                                                strokeWidth="40"
                                                strokeDasharray="60 440"
                                                strokeDashoffset="-441"
                                                transform="rotate(-90 150 150)"
                                                className={classes.chartSegment}
                                            />
                                        </svg>
                                    </div>

                                    <div className={classes.chartLegend}>
                                        {spendingData.map((item, index) => (
                                            <div key={index} className={classes.legendItem}>
                                                <div className={classes.legendColor} style={{ backgroundColor: ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'][index] }}></div>
                                                <div className={classes.legendContent}>
                                                    <div className={classes.legendLabel}>{item.name}</div>
                                                    <div className={classes.legendValue}>${item.value.toLocaleString()}</div>
                                                    <div className={classes.legendPercentage}>{item.percentage}%</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Monthly Trend & Goal */}
                    <div className={classes.sidebarContent}>
                        <div className={classes.trendCard}>
                            <h3 className={classes.sideCardTitle}>Monthly Trend</h3>
                            <div className={classes.trendChart}>
                                <div className={classes.trendLine}></div>
                                <div className={classes.trendValue}>+12.3%</div>
                                <div className={classes.trendLabel}>Growth this month</div>
                            </div>
                        </div>

                        <div className={classes.goalCard}>
                            <div className={classes.goalHeader}>
                                <span className={classes.goalIcon}>🎯</span>
                                <h3 className={classes.sideCardTitle}>Emergency Fund</h3>
                            </div>
                            <div className={classes.goalProgress}>
                                <div className={classes.goalValues}>
                                    <span className={classes.goalCurrent}>$8,500</span>
                                    <span className={classes.goalTarget}>/ $15,000</span>
                                </div>
                                <div className={classes.progressBar}>
                                    <div className={classes.progressFill} style={{ width: '57%' }}></div>
                                </div>
                                <div className={classes.goalStatus}>57% Complete • $6,500 to go</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transactions Section */}
                <div className={classes.transactionsSection}>
                    <div className={classes.premiumCard}>
                        <div className={classes.cardHeader}>
                            <h2 className={classes.cardTitle}>Recent Transactions</h2>
                            <button className={classes.viewAllBtn}>View All →</button>
                        </div>

                        <div className={classes.premiumTransactionsTable}>
                            <div className={classes.tableHeader}>
                                <div className={classes.headerCell}>Date</div>
                                <div className={classes.headerCell}>Description</div>
                                <div className={classes.headerCell}>Category</div>
                                <div className={classes.headerCell}>Amount</div>
                                <div className={classes.headerCell}>Account</div>
                            </div>
                            <div className={classes.tableBody}>
                                {recentTransactions.length > 0 ? (
                                    recentTransactions.map((transaction) => (
                                        <div key={transaction.id} className={classes.tableRow}>
                                            <div className={classes.tableCell}>
                                                {new Date(transaction.date).toLocaleDateString()}
                                            </div>
                                            <div className={classes.tableCell}>
                                                <div className={classes.transactionDesc}>{transaction.description}</div>
                                            </div>
                                            <div className={classes.tableCell}>
                                                <span className={`${classes.categoryTag} ${transaction.category === 'Income' ? classes.incomeTag : classes.expenseTag}`}>
                                                    {transaction.category}
                                                </span>
                                            </div>
                                            <div className={classes.tableCell}>
                                                <span className={`${classes.amount} ${transaction.amount > 0 ? classes.positive : classes.negative}`}>
                                                    {transaction.amount > 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className={classes.tableCell}>
                                                <div className={classes.accountName}>{transaction.account}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className={classes.emptyState}>
                                        <div className={classes.emptyIcon}>📊</div>
                                        <h3 className={classes.emptyTitle}>No transactions yet</h3>
                                        <p className={classes.emptyDescription}>Connect your accounts to start tracking your spending</p>
                                        <button className={classes.connectBtn}>Connect Account</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Insights;